import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { ProjectTimeList } from "../project-time-list";

export const TimeRegistrationTab = ({
  project,
  canManageProjectTime = false,
  showHourlyCosts = true,
  restrictHoursToCurrentUserId = null,
}) => {
  const { t } = useTranslation();
  const [showCostCalculation, setShowCostCalculation] = useState(false);

  const itemState = useSelector((store) => store.hours);
  const { data: timeEntries } = itemState;

  // Calculate hourly costs from time entries (only when cost UI is enabled)
  const calculateHourlyCosts = () => {
    if (!timeEntries || timeEntries.length === 0) return {};

    const userCosts = {};
    const included = (timeEntries || []).filter((e) => e.approved);

    included.forEach((entry) => {
      const userId = entry.user?.id;
      const userName = entry.user?.displayTitle || 'Ukjent bruker';
      const hours = parseFloat(entry.time) || 0;
      const hourlyRate = parseFloat(entry.effective_hourly_rate) || 0;
      const hourCostRate = parseFloat(entry.effective_hour_cost_rate) || 0;
      
      if (!userCosts[userId]) {
        userCosts[userId] = {
          user: { id: userId, name: userName },
          totalHours: 0,
          totalHourlyCost: 0,
          totalHourCostRate: 0,
          hasValidRate: hourlyRate > 0,
          entries: []
        };
      }
      
      userCosts[userId].totalHours += hours;
      userCosts[userId].totalHourlyCost += hours * hourlyRate;
      userCosts[userId].totalHourCostRate += hours * hourCostRate;
      userCosts[userId].entries.push(entry);
    });

    return userCosts;
  };

  const hourlyCosts = showHourlyCosts ? calculateHourlyCosts() : {};
  const totalProjectCost = showHourlyCosts
    ? Object.values(hourlyCosts).reduce((sum, user) => sum + user.totalHourCostRate, 0)
    : 0;
  const hasUsersWithoutRates = showHourlyCosts
    ? Object.values(hourlyCosts).some((user) => !user.hasValidRate)
    : false;

  return (
    <div className="space-y-6">
      {/* Hourly Cost Calculation */}
      {showHourlyCosts ? (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t("project.hourly_costs")}
          </h3>
          <button
            onClick={() => setShowCostCalculation(!showCostCalculation)}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
            {showCostCalculation ? 'Skjul beregning' : 'Vis beregning'}
          </button>
        </div>

        {/* Cost Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-sm font-medium text-blue-900">Totalt timer</span>
            </div>
            <p className="text-2xl font-bold text-blue-900 mt-1">
              {Object.values(hourlyCosts).reduce((sum, user) => sum + user.totalHours, 0).toFixed(1)}h
            </p>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm font-medium text-green-900">Total kostnad</span>
            </div>
            <p className="text-2xl font-bold text-green-900 mt-1">
              {totalProjectCost.toLocaleString('no-NO')} NOK
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="text-sm font-medium text-yellow-900">Mangler timekostnad</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900 mt-1">
              {Object.values(hourlyCosts).filter(user => !user.hasValidRate).length}
            </p>
          </div>
        </div>

        {/* Warning for users without rates */}
        {hasUsersWithoutRates && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t("project.hourly_costs.warning_title")}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{t("project.hourly_costs.missing_rate_warning")}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Cost Calculation */}
        {showCostCalculation && (
          <div className="mt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Detaljert beregning per ansatt
            </h4>
            <div className="space-y-3">
              {Object.values(hourlyCosts).map((userCost) => (
                <div key={userCost.user.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">
                        {userCost.user.name}
                      </span>
                      {!userCost.hasValidRate && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {t("project.hourly_costs.missing_rate_warning")}
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {userCost.totalHours.toFixed(1)}h × {userCost.entries[0]?.effective_hour_cost_rate || 0} NOK
                      </p>
                      <p className="font-medium text-gray-900">
                        {userCost.totalHourCostRate.toLocaleString('no-NO')} NOK
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      ) : null}

      {/* Time Registration Overview */}
      <div className="bg-white shadow rounded-lg p-6">
        <ProjectTimeList
          project={project}
          canManageProjectTime={canManageProjectTime}
          restrictHoursToCurrentUserId={restrictHoursToCurrentUserId}
        />
      </div>
    </div>
  );
};
